import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { DEFAULT_LAYOUT, DEFAULT_WIDGETS } from "../../components/widgets/widgetsConfig";
import LineGraph from "../../components/shared/lineGraph/lineGraph";
import BarChartGeneric from "../../components/shared/barChart/barChart";
import DataDogHexHeatmap from "../../components/dataDog/dataDogHexHeatmap";
import { callAPI } from "../../functions/api";
import { devInfo } from "../../functions/logging";
import { useTheme } from "@mui/material/styles";
import { CustomViewSideBar } from "./customView.sideBar";
import AIInsightsModal from "../../components/modals/aIInsightsModal";
import { prompts } from "../../ai/prompts";
import { DownloadExcel } from "../../functions/downloadExcel";
import localforage from "localforage";
import { useUser } from "../../functions/userContext";
import Ec2DetailsModal from "../../components/modals/ec2DetailsModal";
import { Tabs } from "../../components/shared/tabs/tabs";
import NoticesList from "../../components/notices/noticesList";

const ResponsiveGridLayout = WidthProvider(Responsive);

const MAX_WIDGETS = 8;
const WIDGETS_PER_ROW = 4;
const ROW_UNIT = 1;

const CustomView = () => {
  const theme = useTheme();

  const [selectedWidgets, setSelectedWidgets] = useState([]);
  const [layouts, setLayouts] = useState({ lg: [] });
  const [data, setData] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeInsight, setActiveInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingEc2s, setLoadingEc2s] = useState(true);
  const [loadingPlatformAvailability, setLoadingPlatformAvailability] = useState(true);
  const [selectedHost, setSelectedDataDogHostData] = useState(null);
  const rowHeight = Math.floor((window.innerHeight - 160) / 2 / ROW_UNIT);
  const [selectedEc2, setSelectedEc2] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const { setCurrentTab } = useUser();

  const tabTitles = ["Custom View", "Notices"];

  useEffect(() => {
    if (tabTitles[activeTab]) {
      setCurrentTab(tabTitles[activeTab]);
    }
    return () => setCurrentTab(null);
  }, [activeTab, setCurrentTab]);

  const generateDefaultLayout = (widgets) => {
    return widgets.map((widget, idx) => {
      const id = widget.id || widget;
      const base = DEFAULT_LAYOUT.find((l) => l.i === id) || {
        i: id,
        x: (idx % WIDGETS_PER_ROW) * 3,
        y: Math.floor(idx / WIDGETS_PER_ROW),
        w: 3,
        rowSpan: 1,
      };

      const h = (base.rowSpan || 1) * ROW_UNIT;

      return {
        i: id,
        x: base.x,
        y: base.y * ROW_UNIT,
        w: base.w,
        h,
        minH: h,
        maxH: h * 3,
        minW: 3,
        maxW: 12,
      };
    });
  };

  const handleHexLClicked = async (item) => {
    const response = await callAPI(
      `assets/ec2/ec2bydatadoghost?hostname=${item.host}`,
      "GET",
      null,
      true
    ).then((data) => {
      data.cpu = item.cpu;
      data.ram = item.ram;
      data.disk = item.disk;

      setSelectedEc2(data);
    });
  };

  const { setShowTabs, showTabs } = useUser();

  // ✅ Load saved state
  useEffect(() => {
    setShowTabs(false);
    const loadInitialState = async () => {
      const storedWidgets = await localforage.getItem("selectedWidgets");
      let parsedWidgets = storedWidgets ? JSON.parse(storedWidgets) : DEFAULT_WIDGETS;

      // Migrate old platformAvailability to jiraThroughput
      parsedWidgets = parsedWidgets.map((w) =>
        w.id === "platformAvailability" ? { ...w, id: "jiraThroughput" } : w
      );

      setSelectedWidgets(parsedWidgets);

      const storedLayouts = await localforage.getItem("widgetLayouts");
      let parsedLayouts = storedLayouts
        ? JSON.parse(storedLayouts)
        : { lg: generateDefaultLayout(parsedWidgets) };

      // Migrate layout items
      if (parsedLayouts.lg) {
        parsedLayouts.lg = parsedLayouts.lg.map((item) =>
          item.i === "platformAvailability" ? { ...item, i: "jiraThroughput" } : item
        );
      }

      setLayouts(parsedLayouts);

      setLoading(false);
    };

    loadInitialState();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!selectedWidgets.length) return;

    const fetchWidgetData = async (id) => {
      try {
        let result;
        switch (id) {
          case "jiraThroughput":
            result = await callAPI(
              "ticketing/jira/team-performance-trends",
              "GET",
              null,
              true
            ).then((data) => {
              // Transform data to match LineGraph format
              const totalByMonth = {};
              const teamData = {};

              data.forEach((team) => {
                teamData[team.team] = team.trend.map((point) => ({
                  month: point.month,
                  value: point.issuesResolved,
                  storyPoints: point.storyPoints,
                  cycleTime: point.averageCycleTime,
                }));

                // Accumulate totals across all teams
                team.trend.forEach((point) => {
                  if (!totalByMonth[point.month]) {
                    totalByMonth[point.month] = {
                      value: 0,
                      storyPoints: 0,
                      cycleTimeSum: 0,
                      cycleTimeCount: 0,
                    };
                  }
                  totalByMonth[point.month].value += point.issuesResolved;
                  totalByMonth[point.month].storyPoints += point.storyPoints;
                  if (point.averageCycleTime) {
                    totalByMonth[point.month].cycleTimeSum += point.averageCycleTime;
                    totalByMonth[point.month].cycleTimeCount++;
                  }
                });
              });

              // Build transformed object with Total first, then individual teams
              const transformed = {
                Total: Object.keys(totalByMonth)
                  .sort((a, b) => new Date(a) - new Date(b))
                  .map((month) => ({
                    month,
                    value: totalByMonth[month].value,
                    storyPoints: totalByMonth[month].storyPoints,
                    cycleTime:
                      totalByMonth[month].cycleTimeCount > 0
                        ? totalByMonth[month].cycleTimeSum / totalByMonth[month].cycleTimeCount
                        : 0,
                  })),
                ...teamData,
              };

              setLoadingPlatformAvailability(false);
              return transformed;
            });
            break;
          case "vulnerabilities":
            // Get historical Tenable trends data
            const end = new Date();
            const start = new Date();
            start.setDate(end.getDate() - 60); // Last 60 days

            const params = new URLSearchParams({
              start: start.toISOString(),
              end: end.toISOString(),
            }).toString();

            result = await callAPI(`vulnerabilities/tenable/trends?${params}`, "GET", null, true);

            break;
          case "vendorSpend":
            const costTrendData = await callAPI("CloudCosts/costs", "GET", null, true);

            // Extract AWS total from awsCosts.lobCosts.Total
            const awsTrend =
              costTrendData?.awsCosts?.lobCosts?.Total ||
              costTrendData?.awsCosts?.lobCosts?.TOTAL ||
              costTrendData?.lobCosts?.Total ||
              costTrendData?.lobCosts?.TOTAL ||
              [];

            result = {
              AWS: awsTrend,
            };

            // Extract GCP total from dynamic cloud costs (exclude months with no data)
            const gcpTrend = (costTrendData?.gcpCosts?.lobCosts?.Total || [])
              .filter((m) => m.value !== null && m.value !== undefined);
            if (gcpTrend.length > 0) {
              result["GCP"] = gcpTrend;
            }

            // Extract Azure total from dynamic cloud costs (exclude months with no data)
            const azureTrend = (costTrendData?.azureCosts?.lobCosts?.Total || [])
              .filter((m) => m.value !== null && m.value !== undefined);
            if (azureTrend.length > 0) {
              result["Azure"] = azureTrend;
            }

            break;
          case "ec2ByOs":
            result = await callAPI("assets/ec2/ec2sbyos", "GET", null, true).then((data) => {
              setLoadingEc2s(false);
              return data;
            });
            break;
          case "datadogHeatMap":
            result = await callAPI("logging/datadog/datadogheatmap", "GET", null, true);
            break;
          case "jiraTrend":
            result = await callAPI("ticketing/jira/team-performance-trends", "GET", null, true);
            break;
          default:
            result = null;
        }

        if (result !== undefined) {
          devInfo(`Fetched data for ${id}`, result);
          setData((prev) => ({ ...prev, [id]: result }));
        }
      } catch (err) {
        console.error(`Error fetching ${id}`, err);
      } finally {
        setLoading(false);
      }
    };

    selectedWidgets.forEach((widget) => {
      fetchWidgetData(widget.id);
    });
  }, [selectedWidgets]);

  // ✅ Save to localforage
  useEffect(() => {
    if (!selectedWidgets.length) return;
    localforage.setItem("selectedWidgets", JSON.stringify(selectedWidgets));
    localforage.setItem("widgetLayouts", JSON.stringify(layouts));
  }, [selectedWidgets, layouts]);

  useEffect(() => {
    const listener = () => setDrawerOpen((prev) => !prev);
    window.addEventListener("toggleDashboardConfig", listener);
    return () => window.removeEventListener("toggleDashboardConfig", listener);
  }, []);

  const renderWidget = (widget) => {
    const { id, hasInsights, promptKey } = widget;

    // Helper function to transform data for Excel download
    const handleDownload = (title, widgetData) => {
      if (!widgetData) {
        console.warn("No data available for download");
        return;
      }

      // Transform object-based data (like {AWS: [...], Azure: [...]}) to flat array
      let flatData = [];

      if (Array.isArray(widgetData)) {
        flatData = widgetData;
      } else if (typeof widgetData === "object") {
        // Object with provider keys - flatten to array
        Object.entries(widgetData).forEach(([provider, records]) => {
          if (Array.isArray(records)) {
            records.forEach((record) => {
              flatData.push({
                Provider: provider,
                Month: record.month || record.dateKey || record.timestamp,
                Value: record.value,
                ...record,
              });
            });
          }
        });
      }

      DownloadExcel(title, flatData);
    };

    const commonProps = {
      editMode,
      draggable: true,
      data: data[id],
      insights: hasInsights,
      onInsights: hasInsights
        ? () =>
            setActiveInsight({
              id,
              data: data[id],
              promptKey,
            })
        : undefined,
      onDownload: () =>
        handleDownload(
          id === "jiraThroughput"
            ? "CloudOps Throughput"
            : id === "vendorSpend"
              ? "Vendor Spend"
              : id === "vulnerabilities"
                ? "Vulnerabilities by Severity"
                : widget.title || id,
          data[id]
        ),
    };

    switch (id) {
      case "jiraThroughput":
        return (
          <LineGraph
            loadingMessage="Loading CloudOps throughput..."
            defaultProvider="ALL"
            title="CloudOps Throughput"
            splitX={false}
            showAll={true}
            {...commonProps}
          />
        );
      case "vendorSpend":
        return (
          <LineGraph
            loadingMessage="Calculating vendor spend..."
            defaultProvider="ALL"
            title="Vendor Spend"
            splitX={true}
            showAll={true}
            currency
            {...commonProps}
          />
        );
      case "vulnerabilities":
        return (
          <LineGraph
            defaultProvider="MTTR"
            loadingMessage="Processing vulnerabilities data..."
            title="Vulnerabilities by Severity"
            splitX={false}
            showAll={false}
            colorOverrides={{
              Critical: "#BA1650",
              High: "#F9CA00",
              Medium: "#E97F03",
              Low: "#70C930",
              MTTR: "#2196f3",
            }}
            {...commonProps}
          />
        );
      case "jiraTrend":
      // return <JiraPerformanceTrend {...commonProps} />;
      case "ec2ByOs":
        return (
          <BarChartGeneric
            loading={loadingEc2s}
            title="EC2s by OS"
            showBarIcon={true}
            {...commonProps}
          />
        );
      case "datadogHeatMap":
        return <DataDogHexHeatmap onHexClick={handleHexLClicked} {...commonProps} />;
      default:
        return <div>Unknown Widget</div>;
    }
  };

  const canRender = !loading && Array.isArray(layouts?.lg) && selectedWidgets.length > 0;

  return (
    <Box>
      <Tabs activeTab={activeTab} onTabChange={setActiveTab}>
        <Box
          title="Custom View"
          sx={{ padding: 2, paddingBottom: 3, paddingLeft: 1, paddingRight: 0.375 }}
        >
          {canRender && (
            <ResponsiveGridLayout
              className="layout"
              layouts={layouts}
              onLayoutChange={async (layout) => {
                const newLayouts = { ...layouts, lg: layout };
                setLayouts(newLayouts);
                await localforage.setItem("widgetLayouts", JSON.stringify(newLayouts));
              }}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
              cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
              rowHeight={rowHeight}
              margin={[25, 30]}
              isDraggable={editMode}
              isResizable={editMode}
              draggableHandle=".drag-handle"
              useCSSTransforms={true}
              preventCollision={true}
              compactType={null}
              style={{ marginTop: "10px" }}
              maxRows={2}
            >
              {selectedWidgets.map((widget) => {
                const layout = layouts.lg.find((l) => l.i === widget.id) || {
                  i: widget.id,
                  x: 0,
                  y: 0,
                  w: 6,
                  h: ROW_UNIT,
                  minH: ROW_UNIT,
                  maxH: ROW_UNIT,
                  minW: 3,
                  maxW: 12,
                };
                return (
                  <div key={widget.id} data-grid={layout}>
                    {renderWidget(widget)}
                  </div>
                );
              })}
            </ResponsiveGridLayout>
          )}

          <CustomViewSideBar
            selectedWidgets={selectedWidgets}
            setSelectedWidgets={(widgets) => {
              if (widgets.length <= MAX_WIDGETS) setSelectedWidgets(widgets);
            }}
            setEditMode={setEditMode}
            onReset={() => {
              setSelectedWidgets(DEFAULT_WIDGETS);
              setLayouts({ lg: generateDefaultLayout(DEFAULT_WIDGETS) });
            }}
          />

          {activeInsight?.data && (
            <AIInsightsModal
              open={!!activeInsight}
              onClose={() => setActiveInsight(null)}
              data={activeInsight.data}
              keyValue={activeInsight.id}
              prompt={prompts[activeInsight.promptKey]}
            />
          )}

          <Ec2DetailsModal
            open={selectedEc2 != null}
            onClose={() => setSelectedEc2(null)}
            data={selectedEc2}
          />
        </Box>
        <Box title="Notices" sx={{ padding: 2 }}>
          <NoticesList />
        </Box>
      </Tabs>
    </Box>
  );
};

export default CustomView;
